import { Injectable } from '@angular/core';
import { FeedbackService, MsgType } from '../feedback.service';
import { PopoverListItem, PopoverPage } from '../../_template/popover/popover.page';
import { HttpFunctionResult } from '../../_interfaces/HttpFunctionResult';
import { BoardTemplateEditPage } from '../../_pages/boards/board-template-edit/board-template-edit.page';
import { BoardEditorFilter, BoardService, TicketTag } from './board.service';
import { Employee } from '../../_interfaces/Employee';
import { BoardCardView } from '../../_pages/boards/board-card-view-editor/board-card-view-editor.component';
import { HttpService } from '../http.service';
import { BoardStatusViewSelect } from '../../_pages/boards/board-status-view-select/board-status-view-select.component';
import { TicketEditService } from '../ticket-edit.service';
import { AuthService } from '../auth.service';

export interface BoardTemplateBase{
  id: number,
  name: string,
  icon: string,
  public?: boolean,
}

export interface BoardTemplate extends BoardTemplateBase{
  colored?: boolean,
  tags?: TicketTag[]
  employees?: Employee[],
  boardStatusViewSelect?: BoardStatusViewSelect[],
  editorFilter?: BoardEditorFilter,
  boardCardView?: BoardCardView[]
}

@Injectable({
  providedIn: 'root'
})
export class BoardTemplateService {

  public isLoading: boolean = true

  public templates: BoardTemplateBase[] = []

  public activeBoardTemplate: BoardTemplate
  public activeBoardTemplateBase: BoardTemplateBase

  public reloadBoard: () => Promise<void>
  public setBreadcrumButtons: (setEmpty: boolean) => void

  constructor(
    private feedback: FeedbackService,
    private http: HttpService,
    private ticketEditSrv: TicketEditService,
    private authSrv: AuthService,
    private boardService: BoardService
  ) { }

  /**
   * Öffnet das barebietungsmenu mit den Möglichkeiten zum hinzufügen oder zum bearbeiten eines Boards
   * @param event 
   */
  public async openEditMenu(event: MouseEvent){

    const list: PopoverListItem[] = [
      {id: 1, description: "Board hinzufügen", icon: "fa-plus"},
      {id: 2, description: "Board bearbeiten", icon: "fa-hammer"},
      {id: 3, description: "Board löschen", icon: "fa-trash", danger: true},
    ] 

    await this.feedback.openPopover(PopoverPage, event, {list}, async (result: HttpFunctionResult<PopoverListItem>)=>{
      if(!result.success) return
      if(result.data.id === 1) await this.feedback.openModal(BoardTemplateEditPage)
      if(result.data.id === 2) await this.feedback.openModal(BoardTemplateEditPage, {template: this.activeBoardTemplate, isNew: false})
      if(result.data.id === 3) {
        if(!(await this.feedback.confirm("Wollen Sie das Board wirklich unwiederruflich löschen? Alle Filter zum diesem Board werden ebenfalls gelöscht.", "Löschen", "Abbrechen"))) return
        await this.feedback.showLoading(async () => await this.deleteBoardTemplate(this.activeBoardTemplateBase), "Board wird gelöscht...")
      } 
    })
  }

  /**
   * Gibt alle Templates aus dem Server zurück und wählt die erste aus der Liste aus
   * @param activeTemplateId 
   */
  public async getAllBoardTemplates(activeTemplateId?: number): Promise<void>{
    const result: HttpFunctionResult<BoardTemplateBase[]> = await this.http.GET<BoardTemplateBase[]>({path: "boards/templates"})
    if(result.success) {
      this.templates = result.data 
      if(activeTemplateId != undefined && this.templates.find(t => t.id == activeTemplateId)) this.selectBoardTemplate(this.templates.find(t => t.id == activeTemplateId))
      else if(this.templates.length > 0) this.selectBoardTemplate(this.templates[0])
      else this.isLoading = false
    }
  }

  /**
   * Wählt ein Template aus der Liste aus 
   * @param template 
   * @returns 
   */
  public async selectBoardTemplate(template: BoardTemplate): Promise<void>{

    if(!template) return
    this.activeBoardTemplateBase = template
    const templateResult: HttpFunctionResult<BoardTemplate> = await this.http.GET<BoardTemplate>({path: `boards/templates/${template.id}`})  
    
    if(!templateResult.success) return
    
    this.activeBoardTemplate = templateResult.data
    this.boardService.boardView = templateResult.data.boardCardView;
    this.boardService.tags = this.boardService.tags.map(tag => ({...tag, checked: templateResult.data.tags.find(t => t.id == tag.id) ? true : false}))
    this.boardService.selectedEditorFilter = templateResult.data.editorFilter as BoardEditorFilter
    this.boardService.employees = this.boardService.employees.map(employee => ({...employee, checked: templateResult.data.employees.find(e => e.id == employee.id) ? true : false}))
    this.boardService.coloredBoards = templateResult.data.colored

    if(this.setBreadcrumButtons) this.setBreadcrumButtons(false)
    if(this.reloadBoard) this.reloadBoard() 
    
  }

  /**
   * Erstellt ein neues Template
   * @param template 
   * @returns 
   */
  public async createNewTemplate(template: BoardTemplate): Promise<void>{
    const result: HttpFunctionResult<BoardTemplate> = await this.http.POST<BoardTemplate, BoardTemplate>({path: "boards/templates", body: template})
    if(!result.success) return
    this.templates.push(result.data)
    await this.selectBoardTemplate(result.data)
  }

  /**
   * Löscht ein bestehende Template
   * @param template 
   * @returns 
   */
  public async deleteBoardTemplate(template: BoardTemplateBase){
    const result: HttpFunctionResult<any> = await this.http.DELETE<any>({path: `boards/templates/${template.id}`})
    if(!result.success) return
    this.templates = this.templates.filter(t => t.id != template.id)
    if(this.templates.length > 0) {
      this.activeBoardTemplateBase = this.templates[0]
      await this.selectBoardTemplate(this.templates[0])
    }
    if(this.templates.length == 0 && this.setBreadcrumButtons) this.setBreadcrumButtons(true)
  }

  //#region Board Filter Speichern

  /**
   * Updatet die Basic informationen des Templates
   * @param template 
   * @returns 
   */
  public async updateBoardFilterBase(template: BoardTemplate): Promise<HttpFunctionResult<BoardTemplate>>{
    const result: HttpFunctionResult<BoardTemplate> = await this.http.PUT<BoardTemplate, BoardTemplate>({path: `boards/templates/${template.id}`, body: template})
    if(result.success) {
      this.activeBoardTemplateBase.icon = template.icon
      this.activeBoardTemplateBase.name = template.name
      this.activeBoardTemplateBase.public = template.public
      this.boardService.coloredBoards = template.colored
      this.boardService.selectedEditorFilter = template.editorFilter
    }
    return result
  }

  /**
   * Erstellt das Standard-Template wo man selber zugeordnet ist
   */
  public async createDefaultTemplate(){
    await this.createNewTemplate({
      id: 0,
      name: "Mein Board",
      icon: "fa-hammer",
      colored: false,
      tags: [],
      employees: this.ticketEditSrv.employees.filter(e => e.id == this.authSrv.getUser()?.id),
      boardStatusViewSelect: [],
      editorFilter: BoardEditorFilter.ALL,
      boardCardView: []
    })
  }

  /**
   * Updatet die Ticket Tags des Templates
   * @param tags 
   * @param templateId 
   * @returns 
   */
  public async updateBoardFilterTags(tags: TicketTag[], templateId: number): Promise<HttpFunctionResult<TicketTag[]>>{
    const result: HttpFunctionResult<TicketTag[]> = await this.http.PUT<TicketTag[], TicketTag[]>({path: `boards/templates/${templateId}/tags`, body: tags})
    if(result.success) 
      this.boardService.tags.forEach(tag => tag.checked = result.data.find(t => t.id == tag.id) ? true : false)
    return result
  }

  /**
   * Updatet die zugeiesenen Mitarbeiter des Templates
   * @param employees 
   * @param templateId 
   * @returns 
   */
  public async updateBoardFilterEmployees(employees: Employee[], templateId: number): Promise<HttpFunctionResult<Employee[]>>{
    const result: HttpFunctionResult<Employee[]> = await this.http.PUT<Employee[], Employee[]>({path: `boards/templates/${templateId}/employees`, body: employees})
    if(result.success) 
        this.boardService.employees.forEach(employee => employee.checked = result.data.find(e => e.id == employee.id) ? true : false)
    return result
  }

  /**
   * Updatete die welche status ausgeblendet werden sollen
   * @param viewSelect 
   * @param templateId 
   * @returns 
   */
  public async updateBoardFilterStatusViewSelect(viewSelect: BoardStatusViewSelect[], templateId: number): Promise<HttpFunctionResult<BoardStatusViewSelect[]>>{
    const result: HttpFunctionResult<BoardStatusViewSelect[]> = await this.http.PUT<BoardStatusViewSelect[], BoardStatusViewSelect[]>({path: `boards/templates/${templateId}/status-view-select`, body: viewSelect})
    return result
  }

  /**
   * Updatete welche details auf den Karten angezeigt werden sollen
   * @param cardView 
   * @param templateId 
   * @returns 
   */
  public async updateBoardFilterCardView(cardView: BoardCardView[], templateId: number): Promise<HttpFunctionResult<BoardCardView[]>>{
    const result: HttpFunctionResult<BoardCardView[]> = await this.http.PUT<BoardCardView[], BoardCardView[]>({path: `boards/templates/${templateId}/card-view`, body: cardView})
    if(result.success) 
      this.boardService.boardView = result.data
    return result
  }

  //#endregion


}
